
// AI Verification Service for Bluetooth$ App
// This simulates an AI-based card verification system

export type CardDetails = {
  cardNumber: string;
  cardholderName: string;
  expiryDate: string;
  cvv: string;
};

export type VerificationResult = {
  success: boolean;
  message: string;
  score?: number;
  riskLevel?: 'low' | 'medium' | 'high';
  stripeReady?: boolean;
  stripeResponse?: any;
  last4?: string;
  cardBrand?: string;
};

// Basic validation checks
const validateCardNumber = (cardNumber: string): boolean => {
  // Remove spaces and non-digit characters
  const digitsOnly = cardNumber.replace(/\D/g, '');
  
  // Check if length is valid (most cards are 13-19 digits)
  if (digitsOnly.length < 13 || digitsOnly.length > 19) {
    return false;
  }
  
  // Implement Luhn algorithm for basic card number validation
  let sum = 0;
  let shouldDouble = false;
  
  // Loop from right to left
  for (let i = digitsOnly.length - 1; i >= 0; i--) {
    let digit = parseInt(digitsOnly.charAt(i));
    
    if (shouldDouble) {
      digit *= 2;
      if (digit > 9) {
        digit -= 9;
      }
    }
    
    sum += digit;
    shouldDouble = !shouldDouble;
  }
  
  return sum % 10 === 0;
};

const validateExpiryDate = (expiryDate: string): boolean => {
  // Expected format: MM/YY
  const regex = /^(0[1-9]|1[0-2])\/([0-9]{2})$/;
  if (!regex.test(expiryDate)) {
    return false;
  }
  
  const [month, year] = expiryDate.split('/').map(part => parseInt(part, 10));
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear() % 100; // Get last 2 digits
  const currentMonth = currentDate.getMonth() + 1; // Months are 0-indexed
  
  // Check if card is expired
  if (year < currentYear || (year === currentYear && month < currentMonth)) {
    return false;
  }
  
  return true;
};

const validateCVV = (cvv: string): boolean => {
  // CVV should be 3-4 digits
  return /^[0-9]{3,4}$/.test(cvv);
};

export const verifyCardWithAI = async (cardDetails: CardDetails): Promise<VerificationResult> => {
  return new Promise((resolve) => {
    // Simulate network delay
    setTimeout(() => {
      try {
        // Basic validations
        if (!validateCardNumber(cardDetails.cardNumber)) {
          resolve({
            success: false,
            message: 'Invalid card number',
            score: 0,
            riskLevel: 'high',
            stripeReady: false
          });
          return;
        }
        
        if (!validateExpiryDate(cardDetails.expiryDate)) {
          resolve({
            success: false,
            message: 'Card expired or invalid expiry date format',
            score: 0,
            riskLevel: 'high',
            stripeReady: false
          });
          return;
        }
        
        if (!validateCVV(cardDetails.cvv)) {
          resolve({
            success: false,
            message: 'Invalid CVV code',
            score: 0,
            riskLevel: 'high',
            stripeReady: false
          });
          return;
        }
        
        // AI Risk assessment simulation
        // In a real system, this would call an actual AI model
        const lastFour = cardDetails.cardNumber.slice(-4);
        let score = Math.floor(Math.random() * 30) + 70; // Random score between 70 and 99
        
        // Simulated risk factors
        if (cardDetails.cardholderName.length < 5) {
          score -= 15; // Short names are suspicious
        }
        
        let riskLevel: 'low' | 'medium' | 'high';
        if (score >= 85) {
          riskLevel = 'low';
        } else if (score >= 70) {
          riskLevel = 'medium';
        } else {
          riskLevel = 'high';
        }
        
        resolve({
          success: score >= 70,
          message: score >= 70 
            ? `Card verification successful (Score: ${score})` 
            : `Card verification failed. Risk too high (Score: ${score})`,
          score,
          riskLevel,
          stripeReady: score >= 80, // Set stripeReady based on high confidence
          last4: lastFour,
          cardBrand: getCardType(cardDetails.cardNumber)
        });
        
      } catch (error) {
        resolve({
          success: false,
          message: 'Verification system error',
          score: 0,
          riskLevel: 'high',
          stripeReady: false
        });
      }
    }, 1500); // 1.5 second simulated processing time
  });
};

// Export additional utility functions for card formatting, etc.
export const formatCardNumber = (cardNumber: string): string => {
  const digitsOnly = cardNumber.replace(/\D/g, '');
  const groups = [];
  
  for (let i = 0; i < digitsOnly.length; i += 4) {
    groups.push(digitsOnly.slice(i, i + 4));
  }
  
  return groups.join(' ');
};

export const getCardType = (cardNumber: string): string => {
  const firstDigit = cardNumber.charAt(0);
  const firstTwoDigits = parseInt(cardNumber.substring(0, 2));
  
  if (firstDigit === '4') {
    return 'Visa';
  } else if (firstTwoDigits >= 51 && firstTwoDigits <= 55) {
    return 'MasterCard';
  } else if (firstTwoDigits === 34 || firstTwoDigits === 37) {
    return 'American Express';
  } else if (firstTwoDigits === 65 || firstTwoDigits === 60) {
    return 'Discover';
  }
  
  return 'Unknown';
};
